import { useQuery } from '@tanstack/react-query';
import { Input } from '@/components/ui';
import { patchClientConnections, getClientCampaigns } from '@/data/client';
import type { ClientFormProps } from './types';
import { CONNECTION_TABS, connectionsFromClient, pickConnections } from './data';
import { useDraft } from './useDraft';
import { SaveFooter } from './SaveFooter';

const KEYS = CONNECTION_TABS.ppc;

/* Everything campaignIQ reads for one client. Signal Return has its own section
   and save below this on the same tab. */
export function PpcForm({ client, onSaved, onDirtyChange }: ClientFormProps) {
  const f = useDraft({
    formId: 'ppc',
    initial: connectionsFromClient(client),
    keys: KEYS,
    persist: (d) => patchClientConnections(client.orgId, pickConnections(d, KEYS)),
    onSaved, onDirtyChange,
  });
  const { draft: conn, set: setC } = f;

  /* Two websites can share one Ads account; the sync copies every campaign into
     both. Unticking a campaign here removes it from THIS client's campaignIQ —
     spend, clicks, conversions and attributed leads — without touching the other
     site. Everything is ticked by default, including campaigns that appear later. */
  const { data: camp, isLoading: campLoading } = useQuery({ queryKey: ['admin', 'campaigns', client.orgId], queryFn: () => getClientCampaigns(client.orgId) });
  const campaigns = camp?.campaigns ?? [];
  const excluded = new Set(conn.excludedCampaignIds);
  const toggle = (id: string, included: boolean) => {
    const next = new Set(excluded);
    if (included) next.delete(id); else next.add(id);
    setC('excludedCampaignIds', [...next]);
  };
  const includedCount = campaigns.filter((x) => !excluded.has(x.id)).length;

  return (
    <div className="admin-form">
      <div className="admin-form__body">
        <section className="admin-sect">
          <h4 className="admin-sect__title">Google Ads</h4>
          <div className="admin-prof">
            <div className="admin-prof__row">
              <Input
                label="Google Ads customer ID"
                hint="Digits only, no dashes. Optional."
                placeholder="optional"
                value={conn.googleAdsCustomerId}
                onChange={(e) => setC('googleAdsCustomerId', e.target.value)}
              />
              <Input
                label="Monthly ad-spend goal ($)"
                hint="Admin only. campaignIQ alerts if spend is >10% off this (scaled to the range)."
                placeholder="e.g. 2000"
                inputMode="decimal"
                value={conn.monthlyAdSpendGoal}
                onChange={(e) => setC('monthlyAdSpendGoal', e.target.value)}
              />
              <Input
                label="Billing date (day of month)"
                hint="Admin only. Day the ad account bills, 1–31 (e.g. 6). Shown on campaignIQ."
                placeholder="e.g. 6"
                inputMode="numeric"
                value={conn.adBillingDay}
                onChange={(e) => setC('adBillingDay', e.target.value)}
              />
            </div>
          </div>
        </section>

        <section className="admin-sect">
          <h4 className="admin-sect__title">Campaigns counted for this site</h4>
          <p className="dash-muted admin-sect__note">
            All campaigns in the Ads account count by default. If this account also runs ads for another website, untick the campaigns that belong to it — their spend, clicks, conversions and attributed leads leave this client&rsquo;s campaignIQ and monthly report. New campaigns are always counted until unticked.
          </p>
          {!conn.googleAdsCustomerId.trim() ? (
            <p className="dash-muted">Enter a Google Ads customer ID above; campaigns appear here after the first hourly sync.</p>
          ) : campLoading ? (
            <p className="dash-muted">Loading campaigns…</p>
          ) : campaigns.length === 0 ? (
            <p className="dash-muted">No campaigns synced yet — they appear after the first hourly sync of this account.</p>
          ) : (
            <>
              <p className="dash-muted" style={{ marginBottom: 8 }}>{includedCount} of {campaigns.length} counted.</p>
              <div className="admin-camps">
                {campaigns.map((x) => {
                  const on = !excluded.has(x.id);
                  return (
                    <label key={x.id} className={`admin-camp ${on ? '' : 'admin-camp--off'}`.trim()}>
                      <input type="checkbox" checked={on} onChange={(e) => toggle(x.id, e.target.checked)} />
                      <span className="admin-camp__name">{x.name}</span>
                      <span className="admin-camp__meta">{x.status ? x.status.toLowerCase() : ''}{x.cost90d ? ` · $${x.cost90d.toLocaleString(undefined, { maximumFractionDigits: 0 })} / 90d` : ''}</span>
                    </label>
                  );
                })}
              </div>
            </>
          )}
        </section>
      </div>
      <SaveFooter dirty={f.dirty} saving={f.saving} err={f.err} onSave={f.save} label="Save Google Ads settings" sticky={false} />
    </div>
  );
}
